/**
 * A one-shot handoff between the "New template" / "New dashboard"
 * dialog and the AI designer that opens right after creation.
 *
 * When the user picks a framework, its `starterPrompt` should land in
 * the chat textarea of the designer so they can review, tweak, and
 * send. The dialog doesn't know about the designer's state, and the
 * designer is a sibling route — so we stash the prompt in
 * sessionStorage keyed by artifact id, and the designer consumes +
 * clears it on first mount.
 *
 * sessionStorage (not localStorage) is deliberate: this is strictly a
 * handoff token. If the user reloads or comes back later, we don't
 * want a stale prompt surprising them.
 */

const PREFIX_TEMPLATE = 'canopy.seed.report-template.';
const PREFIX_DASHBOARD = 'canopy.seed.dashboard.';

type Surface = 'report-template' | 'dashboard';

function keyFor(surface: Surface, id: string): string {
  return (surface === 'report-template' ? PREFIX_TEMPLATE : PREFIX_DASHBOARD) + id;
}

export function stashStarterPrompt(surface: Surface, id: string, prompt: string): void {
  try {
    sessionStorage.setItem(keyFor(surface, id), prompt);
  } catch {
    /* storage unavailable — non-fatal, designer just starts blank */
  }
}

/**
 * Read + CLEAR the starter prompt for this artifact. The clear is
 * intentional: we only ever want to pre-fill once per creation.
 */
export function consumeStarterPrompt(surface: Surface, id: string): string | null {
  try {
    const key = keyFor(surface, id);
    const value = sessionStorage.getItem(key);
    if (value) sessionStorage.removeItem(key);
    return value;
  } catch {
    return null;
  }
}
